import { z } from 'zod';

/**
 * Domain schemas for webhook entities
 * Using Base+Variants pattern for scalability
 */

/**
 * Base schema for webhook acknowledgment responses
 */
const BaseWebhookResponseSchema = z.object({
  received: z.boolean(),
  message: z.string().optional(),
  timestamp: z.iso.datetime().optional(),
});

/**
 * WhatsApp webhook verification request (GET /webhooks/whatsapp)
 */
const WhatsAppVerificationRequestSchema = z.object({
  'hub.mode': z.literal('subscribe'),
  'hub.verify_token': z.string(),
  'hub.challenge': z.string(),
});

/**
 * WhatsApp message schema
 */
const WhatsAppMessageSchema = z.object({
  from: z.string(),
  id: z.string(),
  timestamp: z.string(),
  type: z.enum([
    'text',
    'button',
    'interactive',
    'image',
    'audio',
    'document',
    'order',
    'system',
    'unknown',
  ]),
  text: z
    .object({
      body: z.string(),
    })
    .optional(),
  button: z
    .object({
      text: z.string(),
      payload: z.string().optional(),
    })
    .optional(),
  interactive: z
    .object({
      type: z.enum(['button_reply', 'list_reply']),
      button_reply: z
        .object({
          id: z.string(),
          title: z.string(),
        })
        .optional(),
    })
    .optional(),
});

/**
 * WhatsApp status schema
 */
const WhatsAppStatusSchema = z.object({
  id: z.string(),
  status: z.enum(['sent', 'delivered', 'read', 'failed']),
  timestamp: z.string(),
  recipient_id: z.string(),
  conversation: z
    .object({
      id: z.string(),
      origin: z
        .object({
          type: z.string(),
        })
        .optional(),
    })
    .optional(),
  pricing: z
    .object({
      billable: z.boolean(),
      category: z.string(),
    })
    .optional(),
  errors: z
    .array(
      z.object({
        code: z.number(),
        title: z.string(),
        message: z.string().optional(),
        error_data: z.record(z.string(), z.unknown()).optional(),
      })
    )
    .optional(),
});

/**
 * WhatsApp webhook payload schema (from Meta Business API)
 */
export const WhatsAppWebhookSchema = z.object({
  object: z.literal('whatsapp_business_account'),
  entry: z.array(
    z.object({
      id: z.string(),
      changes: z.array(
        z.object({
          field: z.literal('messages'),
          value: z.object({
            messaging_product: z.literal('whatsapp'),
            metadata: z.object({
              display_phone_number: z.string(),
              phone_number_id: z.string(),
            }),
            contacts: z
              .array(
                z.object({
                  wa_id: z.string(),
                  profile: z
                    .object({
                      name: z.string().optional(),
                    })
                    .optional(),
                })
              )
              .optional(),
            messages: z.array(WhatsAppMessageSchema).optional(),
            statuses: z.array(WhatsAppStatusSchema).optional(),
          }),
        })
      ),
    })
  ),
});

/**
 * Stripe webhook event schema (basic structure, Stripe handles the rest)
 */
export const StripeWebhookEventSchema = z.object({
  id: z.string(),
  object: z.literal('event'),
  type: z.string(),
  created: z.number(),
  data: z.object({
    object: z.record(z.string(), z.unknown()),
    previous_attributes: z.record(z.string(), z.unknown()).optional(),
  }),
  livemode: z.boolean(),
  pending_webhooks: z.number(),
  request: z
    .object({
      id: z.string().optional(),
      idempotency_key: z.string().optional(),
    })
    .optional(),
});

/**
 * Webhook schema variants for different contexts
 */
export const WebhookSchemas = {
  // Webhook response acknowledgments
  response: BaseWebhookResponseSchema,

  // WhatsApp specific schemas
  whatsappVerification: WhatsAppVerificationRequestSchema,
  whatsappWebhook: WhatsAppWebhookSchema,
  whatsappMessage: WhatsAppMessageSchema,
  whatsappStatus: WhatsAppStatusSchema,

  // Stripe webhook event
  stripeWebhook: StripeWebhookEventSchema,
};

/**
 * Response schemas for webhook operations
 */
export const WebhookResponseSchemas = {
  // Standard webhook acknowledgment
  acknowledgment: z.object({
    received: z.boolean(),
  }),

  // WhatsApp verification challenge response
  verification: z.string(), // Just the challenge string

  // Error responses
  error: z.object({
    error: z.string(),
    message: z.string(),
  }),
};

// Export types
export type WhatsAppWebhook = z.infer<typeof WebhookSchemas.whatsappWebhook>;
export type WhatsAppMessage = z.infer<typeof WebhookSchemas.whatsappMessage>;
export type WhatsAppStatus = z.infer<typeof WebhookSchemas.whatsappStatus>;
export type WhatsAppVerificationRequest = z.infer<
  typeof WebhookSchemas.whatsappVerification
>;
export type StripeWebhookEvent = z.infer<typeof WebhookSchemas.stripeWebhook>;
export type WebhookAcknowledgment = z.infer<
  typeof WebhookResponseSchemas.acknowledgment
>;
export type WebhookVerification = z.infer<
  typeof WebhookResponseSchemas.verification
>;
export type WebhookError = z.infer<typeof WebhookResponseSchemas.error>;
